import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  useEnsureA2pEventStreamMutation,
  useGetPlatformConfigQuery,
  useGetQuietHoursPolicyOptionsQuery,
  useUpdatePlatformConfigMutation,
} from "@/store/admin_api";
import { useRebuildCommunicationItemsMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

const QUIET_HOURS_POLICY_KEY = "compliance.quiet-hours.policy";
const REFERRAL_ENABLED_KEY = "referral.enabled";
const REFERRAL_REWARD_KEY = "referral.reward-amount";

export default function PlatformSettings() {
  const { data: config, isLoading } = useGetPlatformConfigQuery();
  const { data: quietHoursPolicies } = useGetQuietHoursPolicyOptionsQuery();
  const [updateConfig, { isLoading: isSaving }] = useUpdatePlatformConfigMutation();
  const [ensureA2pEventStream, { isLoading: isSettingUpEventStream, data: eventStreamStatus }] =
    useEnsureA2pEventStreamMutation();
  const [rebuildCommunicationItems, { isLoading: isRebuilding }] = useRebuildCommunicationItemsMutation();

  const [quietHoursPolicy, setQuietHoursPolicy] = useState("WARN");
  const [assistedSchedulingUrl, setAssistedSchedulingUrl] = useState("");
  const [referralEnabled, setReferralEnabled] = useState(true);
  const [referralReward, setReferralReward] = useState("50");

  const selectedQuietHoursPolicy = quietHoursPolicies?.find((option) => option.name === quietHoursPolicy);
  const referralRewardValid = Number(referralReward) > 0;

  const handleSaveQuietHours = async () => {
    try {
      await updateConfig({ [QUIET_HOURS_POLICY_KEY]: quietHoursPolicy }).unwrap();
      toast.success("Quiet hours policy saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save the quiet hours policy"));
    }
  };

  const handleSaveAssistedSettings = async () => {
    try {
      await updateConfig({ a2p_assisted_scheduling_url: assistedSchedulingUrl.trim() }).unwrap();
      toast.success("Assisted registration settings saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save assisted registration settings"));
    }
  };

  const handleSaveReferralSettings = async () => {
    try {
      await updateConfig({
        [REFERRAL_ENABLED_KEY]: String(referralEnabled),
        [REFERRAL_REWARD_KEY]: referralReward.trim(),
      }).unwrap();
      toast.success("Referral program saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save the referral program"));
    }
  };

  const handleEnsureA2pEventStream = async () => {
    try {
      const status = await ensureA2pEventStream().unwrap();
      if (status.createdSink || status.createdSubscription) {
        toast.success("Twilio webhook registered. Brand and campaign status changes now arrive as events.");
      } else {
        toast.success("Twilio webhook already registered and active.");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to set up the Twilio webhook"));
    }
  };

  const handleRebuild = async () => {
    try {
      const result = await rebuildCommunicationItems().unwrap();
      toast.success(`Linked ${result.repaired} rows to contacts and refreshed ${result.resynced} items`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Rebuild failed"));
    }
  };

  useEffect(() => {
    if (config) {
      setQuietHoursPolicy(config[QUIET_HOURS_POLICY_KEY] || "WARN");
      setAssistedSchedulingUrl(config.a2p_assisted_scheduling_url || "");
      setReferralEnabled(config[REFERRAL_ENABLED_KEY] !== "false");
      setReferralReward(config[REFERRAL_REWARD_KEY] || "50");
    }
  }, [config]);

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h2 className="text-2xl font-bold mb-1">Settings</h2>
        <p className="text-muted-foreground text-sm">Platform-wide settings and maintenance.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quiet Hours</CardTitle>
          <CardDescription>
            Applies to every outbound call and text, AI agents, power dialer, workflows and manual dialling, for
            organisations that have no policy of their own. Contact hours are 8 AM to 9 PM in the recipient's own
            timezone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="quiet-hours-policy">Default policy</Label>
            <Select value={quietHoursPolicy} onValueChange={setQuietHoursPolicy}>
              <SelectTrigger id="quiet-hours-policy" className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {quietHoursPolicies?.map((option) => (
                  <SelectItem key={option.name} value={option.name}>
                    {option.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{selectedQuietHoursPolicy?.description}</p>
          </div>

          <p className="text-xs text-muted-foreground">
            Automated calls never prompt, they are blocked outright unless the policy is Off. An organisation set to
            its own policy keeps it, stricter or looser, so this is a default rather than a ceiling.
          </p>

          {quietHoursPolicy === "OFF" && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
              <p className="text-sm font-medium">Quiet hours are off platform-wide</p>
              <p className="text-sm text-muted-foreground">
                Every organisation without its own policy can call and text at any hour, with no confirmation and no
                audit record. Intended for testing, change this back before going live.
              </p>
            </div>
          )}

          <Button onClick={() => void handleSaveQuietHours()} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Assisted SMS Registration</CardTitle>
          <CardDescription>
            The scheduling link shown to customers after they purchase assisted registration. The fee itself lives on
            the pricing plans.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-sm">Scheduling link</Label>
            <Input
              placeholder="https://..."
              value={assistedSchedulingUrl}
              onChange={(event) => setAssistedSchedulingUrl(event.target.value)}
            />
          </div>
          <Button onClick={() => void handleSaveAssistedSettings()} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Referral program</CardTitle>
          <CardDescription>
            When an organisation that signed up through a referral link pays its first subscription invoice, it and the
            organisation that referred it each get this much free credit on their prepaid balance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="referral-enabled">Program on</Label>
            <Switch id="referral-enabled" checked={referralEnabled} onCheckedChange={setReferralEnabled} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="referral-reward" className="text-sm">
              Credit for each side ($)
            </Label>
            <Input
              id="referral-reward"
              type="number"
              min="1"
              step="0.01"
              className="w-32"
              value={referralReward}
              onChange={(event) => setReferralReward(event.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Turning it off hides the Refer and earn page and stops new referrals and credits. Credit already added
            stays.
          </p>
          <Button onClick={() => void handleSaveReferralSettings()} disabled={isSaving || !referralRewardValid}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">A2P Status Webhook</CardTitle>
          <CardDescription>
            Registers the Twilio event stream that pushes brand and campaign registration status changes to us the
            moment they happen. Without it, statuses update on an hourly poll. Safe to run repeatedly; it only creates
            what is missing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {eventStreamStatus && (
            <div className="rounded-lg border p-3 text-xs space-y-1">
              <p>
                <span className="font-medium">Webhook URL:</span> {eventStreamStatus.webhookUrl}
              </p>
              <p>
                <span className="font-medium">Sink:</span> {eventStreamStatus.sinkSid}
                {eventStreamStatus.createdSink ? " (created)" : " (existing)"}
              </p>
              <p>
                <span className="font-medium">Subscription:</span> {eventStreamStatus.subscriptionSid}
                {eventStreamStatus.createdSubscription ? " (created)" : " (existing)"}
              </p>
            </div>
          )}
          <Button onClick={() => void handleEnsureA2pEventStream()} disabled={isSettingUpEventStream}>
            {isSettingUpEventStream ? "Checking Twilio..." : "Register Webhook"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inbox items</CardTitle>
          <CardDescription>
            Links calls, texts and conversations that have no contact to one, then refreshes every inbox item from its
            call or text. Run it after a deploy that changes how items are built, or when the inbox looks out of step
            with the calls and texts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => void handleRebuild()} disabled={isRebuilding}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isRebuilding ? "animate-spin" : ""}`} />
            {isRebuilding ? "Rebuilding..." : "Rebuild inbox items"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
